<script setup lang="ts">
import { useDisplay } from 'vuetify';
import useLocale from '../helpers/use-locale';

type Emits = {
  (e: 'confirmed'): void;
};

const emit = defineEmits<Emits>();

const editedName = defineModel<string>();

type Props = {
  canEditName?: boolean;
  nameSize?: 'sm' | 'md' | 'lg';
};
withDefaults(defineProps<Props>(), { canEditName: false, nameSize: 'md' });

const { xs, smAndDown } = useDisplay();
const { t } = useLocale();

const confirmEditing = () => emit('confirmed');

const handleConfirmAndTrack = () => {
  confirmEditing();
};

const nameSizeClass: Record<string, string> = { sm: 'text-h5', md: 'text-h4', lg: 'text-h3' };
</script>

<template>
  <v-text-field
    v-if="canEditName"
    id="input-name-field"
    v-model="editedName"
    :class="['text-h4', 'name-field', xs ? 'mt-1' : 'mt-3']"
    hide-details
    density="compact"
    variant="solo"
    flat
    maxlength="40"
    :aria-label="t('components.label_edit_name')"
    :placeholder="t('components.label_edit_name')"
    aria-describedby="input-name-field"
    @keydown.enter.prevent="handleConfirmAndTrack"
    @blur="confirmEditing"
  >
    <template #label>
      <span class="d-none">{{ t('components.label_edit_name') }}</span>
    </template>
  </v-text-field>
  <span v-else class="mt-3 text-primary collection-title" :class="smAndDown ? 'text-h5' : nameSizeClass[nameSize]">
    {{ editedName }}
  </span>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.name-field {
  :deep(.v-field__input) {
    text-align: center;
    font-size: 1.8rem;
    color: rgb(var(--v-theme-primary));
    padding: 1rem;
  }

  @media #{map.get(v.$display-breakpoints, 'xs')} {
    :deep(.v-field__input) {
      font-size: 1.5rem;
      padding: 0;
    }
  }
}

:deep(.v-field) {
  &.v-field--focused {
    border: 2px solid rgb(var(--v-theme-primary));
    margin: -2px 0;
  }
}

.collection-title {
  overflow-wrap: break-word;
}
</style>
